import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Calendar, Plus, ChevronLeft, ChevronRight, RefreshCw,
  Mail, MessageSquare, Share2, Megaphone, Truck, Bell, Zap, Trash2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  { value: 'email', label: 'Email', icon: Mail, color: '#3B82F6' },
  { value: 'sms', label: 'SMS', icon: MessageSquare, color: '#10B981' },
  { value: 'social', label: 'Social', icon: Share2, color: '#8B5CF6' },
  { value: 'ads', label: 'Ads', icon: Megaphone, color: '#F97316' },
  { value: 'direct_mail', label: 'Direct Mail', icon: Truck, color: '#EF4444' },
  { value: 'push', label: 'Push', icon: Bell, color: '#14B8A6' },
  { value: 'event', label: 'Event', icon: Zap, color: '#F59E0B' },
];

const STATUS_OPTS = ['draft', 'scheduled', 'live', 'completed'];

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

export default function CampaignCalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEvent, setShowEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [form, setForm] = useState({ title: '', channel: 'email', start_date: '', end_date: '', status: 'scheduled', description: '' });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/campaign-calendar/events?month=${monthStr}`);
      setEvents(data.events || []);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  }, [monthStr]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API}/api/campaign-calendar/events`, form);
      toast.success('Event created!');
      setShowCreate(false);
      setForm({ title: '', channel: 'email', start_date: '', end_date: '', status: 'scheduled', description: '' });
      fetchEvents();
    } catch { toast.error('Failed'); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete event?')) return;
    try {
      await axios.delete(`${API}/api/campaign-calendar/events/${id}`);
      toast.success('Deleted');
      fetchEvents();
      setShowEvent(null);
    } catch { toast.error('Failed'); }
  };

  // Build calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.start_date === dateStr);
  };

  const channelCounts = CHANNELS.map(ch => ({ ...ch, count: events.filter(e => e.channel === ch.value).length }));

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="campaign-calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Campaign Calendar<PageInfoTooltip pageKey="campaign-calendar" /></h1>
          <p className="text-gray-medium text-sm mt-1">Plan and visualize all your marketing campaigns</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-event-btn">
          <Plus className="h-4 w-4 mr-1" /> New Event
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Calendar} label="Total Events" value={events.length} color="bg-blue-50 text-blue-600" />
        <StatCard icon={Zap} label="Scheduled" value={events.filter(e => e.status === 'scheduled').length} color="bg-amber-50 text-amber-600" />
        <StatCard icon={Share2} label="Live" value={events.filter(e => e.status === 'live').length} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={Mail} label="Completed" value={events.filter(e => e.status === 'completed').length} color="bg-purple-50 text-purple-600" />
      </div>

      {/* Channel Legend */}
      <div className="flex flex-wrap gap-2">
        {channelCounts.filter(c => c.count > 0).map(ch => (
          <Badge key={ch.value} className="text-xs px-2 py-0.5" style={{ backgroundColor: ch.color + '20', color: ch.color }}>
            {ch.label}: {ch.count}
          </Badge>
        ))}
      </div>

      {/* Calendar */}
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="h-4 w-4" /></Button>
            <h2 className="text-lg font-semibold text-text-primary">{currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
            <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-px bg-gray-100 rounded-lg overflow-hidden">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="bg-gray-50 p-2 text-center text-xs font-medium text-gray-medium">{d}</div>
            ))}
            {days.map((day, i) => {
              const dayEvents = day ? getEventsForDay(day) : [];
              const isToday = day && new Date().toDateString() === new Date(year, month, day).toDateString();
              return (
                <div key={i} className={cn("bg-white min-h-[80px] p-1.5 border-t border-gray-100", !day && "bg-gray-50/50")}>
                  {day && (
                    <>
                      <span className={cn("text-xs font-medium", isToday ? "bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center" : "text-gray-medium")}>{day}</span>
                      <div className="mt-1 space-y-0.5">
                        {dayEvents.slice(0, 3).map(evt => {
                          const ch = CHANNELS.find(c => c.value === evt.channel);
                          return (
                            <div key={evt.id} className="text-[10px] px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80" style={{ backgroundColor: (ch?.color || '#6B7280') + '20', color: ch?.color || '#6B7280' }} onClick={() => setShowEvent(evt)}>
                              {evt.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && <span className="text-[9px] text-gray-400">+{dayEvents.length - 3} more</span>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Campaign Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="event-title" /></div>
            <div><Label>Channel</Label>
              <Select value={form.channel} onValueChange={v => setForm(f => ({ ...f, channel: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map(ch => <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTS.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-event-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Event Dialog */}
      <Dialog open={!!showEvent} onOpenChange={() => setShowEvent(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{showEvent?.title}</DialogTitle></DialogHeader>
          {showEvent && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-medium">Channel:</span> <Badge className="text-[10px]" style={{ backgroundColor: (CHANNELS.find(c => c.value === showEvent.channel)?.color || '#6B7280') + '20', color: CHANNELS.find(c => c.value === showEvent.channel)?.color }}>{showEvent.channel}</Badge></div>
                <div><span className="text-gray-medium">Status:</span> <span className="capitalize font-medium">{showEvent.status}</span></div>
                <div><span className="text-gray-medium">Start:</span> <span>{showEvent.start_date}</span></div>
                <div><span className="text-gray-medium">End:</span> <span>{showEvent.end_date || '—'}</span></div>
              </div>
              {showEvent.description && <p className="text-gray-medium bg-gray-50 p-2 rounded">{showEvent.description}</p>}
              <Button size="sm" variant="destructive" onClick={() => deleteEvent(showEvent.id)}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
